/**
 * Phone number helpers for BasePhoneInput (VAM-294).
 *
 * The form keeps ONE string in international format (`+49 160 1234567`) — that is
 * what validation and the DaVinci `Telephone` field expect. The input shows it split
 * into a country (calling-code dropdown) and the national part.
 */

import {
  AsYouType,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js/max';
import {
  CALLING_CODES,
  PHONE_COUNTRY_BY_CODE,
  getMainCountryForCallingCode,
} from '@/constants/checkout/phoneCountries';

export interface PhoneParts {
  country: CountryCode;
  national: string;
}

const MAX_CALLING_CODE_LENGTH = 3;

const digitsOnly = (value: string): string => value.replace(/\D/g, '');

/** Find the calling code the digits start with (E.164 codes are prefix-free). */
function findCallingCode(digits: string): string | undefined {
  for (let length = 1; length <= MAX_CALLING_CODE_LENGTH; length++) {
    const candidate = digits.slice(0, length);
    if (CALLING_CODES.has(candidate)) return candidate;
  }
  return undefined;
}

/**
 * Pick the country for a calling code: the one the full number belongs to (e.g. a
 * Canadian `+1` number), else the current country if it shares the code, else the
 * code's main country.
 */
function resolveCountry(callingCode: string, digits: string, current: CountryCode): CountryCode {
  const parsedCountry = parsePhoneNumberFromString(`+${digits}`)?.country;
  if (parsedCountry && PHONE_COUNTRY_BY_CODE.has(parsedCountry)) return parsedCountry;
  if (getCountryCallingCode(current) === callingCode) return current;
  return getMainCountryForCallingCode(callingCode) ?? current;
}

/**
 * Interpret what the user typed or pasted into the national field. Text starting with
 * `+` or `00` is treated as a full international number: once its calling code is
 * recognised, the country switches and only the national part is kept. Anything else
 * is returned unchanged for the current country.
 */
export function parsePhoneInput(raw: string, current: CountryCode): PhoneParts {
  const trimmed = raw.trim();
  const international = trimmed.startsWith('00') ? `+${trimmed.slice(2)}` : trimmed;

  if (!international.startsWith('+')) {
    return { country: current, national: raw.trimStart() };
  }

  const digits = digitsOnly(international);
  const callingCode = findCallingCode(digits);

  if (!callingCode) {
    return { country: current, national: raw };
  }

  return {
    country: resolveCountry(callingCode, digits, current),
    national: digits.slice(callingCode.length),
  };
}

/**
 * Combine country + national part into the stored `+49 160 1234567` form. The
 * national trunk prefix is dropped (`0160…` → `+49 160…`); leading zeros that belong
 * to the number (e.g. Italian landlines) are kept.
 */
export function joinPhone(country: CountryCode, national: string): string {
  const digits = digitsOnly(national);
  if (!digits) return '';

  const asYouType = new AsYouType(country);
  asYouType.input(digits);

  return (
    asYouType.getNumber()?.formatInternational() ?? `+${getCountryCallingCode(country)} ${digits}`
  );
}

/**
 * Split a stored `+49 160 1234567` value back into country + national part, e.g. to
 * prefill the field when the user returns to the form. Empty or unrecognised values
 * fall back to `fallbackCountry`.
 */
export function splitPhone(
  value: string | null | undefined,
  fallbackCountry: CountryCode
): PhoneParts {
  if (!value?.trim()) {
    return { country: fallbackCountry, national: '' };
  }

  const parts = parsePhoneInput(value, fallbackCountry);
  if (parts.national === value) return parts;

  return { country: parts.country, national: formatNational(parts.country, parts.national) };
}

/**
 * National part in readable grouping without the calling code (`1601234567` →
 * `160 1234567`); unparsable input is returned as typed.
 */
export function formatNational(country: CountryCode, national: string): string {
  const joined = joinPhone(country, national);
  const prefix = `+${getCountryCallingCode(country)} `;
  return joined.startsWith(prefix) ? joined.slice(prefix.length) : national;
}
